import type { FC, ReactNode } from "react"
import { useMemo } from "react"
import type { NormalizedCacheObject } from "@apollo/client/index.js"
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  HttpLink,
  ApolloLink
} from "@apollo/client/index.js"
import { relayStylePagination } from "@apollo/client/utilities"
import type { StrictTypedTypePolicies } from "~/graphql/apollo"
import type { PublicConfigValues } from "~/providers/ConfigProvider"
import { useConfigContext } from "~/providers/ConfigProvider"
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries"

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined

export const createBackendClient = (config: PublicConfigValues) => {
  if (apolloClient) {
    return apolloClient
  }

  const typePolicies: StrictTypedTypePolicies = {
    Query: {
      fields: {
        // Use the result of locations query for location(id) query.

        location: (_, { args, toReference }) =>
          toReference({ __typename: "Location", id: args?.id })
      }
    },
    ActivityType: {
      fields: {
        futureActivities: relayStylePagination()
      }
    },
    Collection: {
      fields: {
        products: relayStylePagination(["sort", "filters", "first"]),
        picks: relayStylePagination(["filters"])
      }
    }
  }

  const cache = new InMemoryCache({
    typePolicies
  })

  const links = []

  if (isSubtleCryptoDigestSupported()) {
    links.push(
      createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true
      })
    )
  }

  links.push(
    new HttpLink({
      uri: `${
        config.backendProxy ? "/backend_proxy" : config.backendBase
      }/graphql`
    })
  )

  apolloClient = new ApolloClient({
    link: ApolloLink.from(links),
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true
      }
    }
  })

  return apolloClient
}

export const BackendApiProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const config = useConfigContext()

  const client = useMemo(() => createBackendClient(config), [config])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

const isSubtleCryptoDigestSupported = () =>
  Boolean(typeof crypto !== "undefined" && crypto?.subtle.digest)

const sha256 = async (message: string) => {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("")
  return hashHex
}
